import {api, RequestParam} from "@/shared/api";

import {DriveLogStatistics} from "@/features/drivelog/driveLogStatistics";
import {DriveLogStatusPanel} from "@/features/drivelog/driveLogStatusPanel";
import {DriveLogTable} from "@/features/drivelog/driveLogTable";
import {DriveLogDetailsDialog} from "@/features/drivelog/driveLogDetailsDialog";
import {DriveLogDialog} from "@/features/drivelog/driveLogDialog";
import {BaseRecording, BaseRecordingConfig} from "./baseRecording";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {RecordDetailsDialog} from "@/shared/recording/recordDetailsDialog";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {isDriveLogDateTimeChangeAllowed} from "@/shared/securityHelper";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {Car, DriveLog, DriveLogCategory, DriveLogStatus, ReportDriveLog} from "@/shared/timr";
import {RecordedEntryStatus} from "@/shared/timrTypes";
import {mapDriveLogToReportDriveLog} from "@/shared/utils/recordMapper";

export interface DriveLogRecordingConfig extends BaseRecordingConfig<DriveLogStatus> {
	cars: Car[];
	driveLogCategories: DriveLogCategory[];
}

export class DriveLogRecordingPage extends BaseRecording<ReportDriveLog, DriveLogStatus, DriveLog> {
	private static DRIVE_LOG_TABLE_PAGE_LENGTH = BaseRecording.RECORDING_TABLE_PAGE_LENGTH;

	declare private driveLogStatistics: DriveLogStatistics;

	constructor(public config: DriveLogRecordingConfig) {
		super(config);
	}

	protected init() {
		super.init();
		this.recordingDetailsDialog = this.createRecordingDetailsDialog();

		$(".addButton").toggleClass("disabled", !this.isDateTimeChangeAllowed()).click(() => {
			this.addRecording(this.statusPanel.getLastRecording());
		});

		this.driveLogStatistics = new DriveLogStatistics($("#driveLogStatistics"));
	}

	protected showRecording(recording: ReportDriveLog) {
		this.recordingDetailsDialog.show(recording);
	}

	protected createStatusPanel(): DriveLogStatusPanel {
		return new DriveLogStatusPanel({
			panel: $("#driveLogStatusPanel"),
			fetchStatusURL: "recording/getDriveLogStatus.json",
			customFieldDefinitions: this.config.customFieldDefinitions,
			cars: this.config.cars,
			driveLogCategories: this.config.driveLogCategories
		});
	}

	protected createRecordingTable(): RecordingTable<ReportDriveLog> {
		return new DriveLogTable({
			dataUrl: DriveLogTable.RECORDING_DRIVE_LOG_URL,
			element: $("#driveLogTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: false,
			allowResume: true,
			allowSelect: false,
			linkToReport: "driveLog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: false,
			orderable: false,
			clampNote: false,
			allowExpand: false,
			pageLength: DriveLogRecordingPage.DRIVE_LOG_TABLE_PAGE_LENGTH,
			paging: false
		});
	}

	protected createRecordingDetailsDialog(): RecordDetailsDialog<ReportDriveLog> {
		return new DriveLogDetailsDialog({
			title: messages["driveLog.details.header"],
			dialogId: "#driveLogDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportDriveLog) => this.editRecording(recording)
		});
	}

	protected createRecordingDialog(): RecordDialog<ReportDriveLog> {
		return new DriveLogDialog({
			dialogId: "#driveLogDialog",
			users: [],
			cars: this.config.cars,
			driveLogCategories: this.config.driveLogCategories,
			userSelectionEnabled: false,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddDriveLogAllowed()) {
			BootstrapModal.info({
				title: "account.expired.drivelog.title",
				message: "account.expired.drivelog",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected addRecording(lastRecording: DriveLog | null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Drive Log - Add Drive Log", "Drive Log");
		this.recordingDialog.create(lastRecording ? mapDriveLogToReportDriveLog(lastRecording) : null);
	}

	protected resumeRecording(recording: ReportDriveLog) {
		if (recording.status === RecordedEntryStatus.Changeable) {
			api("recording/resumeDriveLog.json")
				.post({
					id: recording.id,
					timestamp: Date.now()
				})
				.done(() => {
					this.reloadTable();
				});
		}
	}

	protected updateStatistics(status: DriveLogStatus) {
		this.driveLogStatistics.updateStatistics(status);
	}

	protected buildFilterParams(): RequestParam[] {
		const endDate = Temporal.Now.plainDateISO();
		const startDate = endDate.subtract({years: 1});
		return [
			...super.buildFilterParams(),
			{name: "endDate", value: getISODate(endDate)},
			{name: "startDate", value: getISODate(startDate)},
		];
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isDriveLogDateTimeChangeAllowed();
	}

	protected getRecordById() {
		// not implemented
	}
}
